// Central query key factory — single source of truth for all TanStack Query cache keys.
// Import from here in every query/mutation hook.
//
// Invalidation guide:
//   queryClient.invalidateQueries({ queryKey: queryKeys.events.all })  → all event queries
//   queryClient.invalidateQueries({ queryKey: queryKeys.events.list(venueId) })  → just the list
//
// Cross-domain invalidation map (mutations should invalidate):
//   create/update event  → events, calendar, dashboard, insights
//   update venue profile → venueProfile, dashboard
//   toggle demo mode     → everything (queryClient.invalidateQueries())
//   create GM report     → gmReports, events, dashboard
//   watchlist add/remove → watchlist, artists
//   send campaign        → emailCampaigns, smsCampaigns, marketing

export const queryKeys = {
  // Auth & identity
  auth: {
    all: ['auth'] as const,
    currentUser: ['auth', 'currentUser'] as const,
  },

  venueProfile: {
    all: ['venueProfile'] as const,
    current: ['venueProfile', 'current'] as const,
  },

  subscription: {
    all: ['subscription'] as const,
    forVenue: (venueId: string) => ['subscription', venueId] as const,
  },

  userRole: {
    all: ['userRole'] as const,
    forVenue: (venueId: string) => ['userRole', venueId] as const,
  },

  // Core data
  events: {
    all: ['events'] as const,
    list: (venueId: string) => ['events', 'list', venueId] as const,
    detail: (eventId: string) => ['events', 'detail', eventId] as const,
    upcoming: (venueId: string) => ['events', 'upcoming', venueId] as const,
  },

  calendar: {
    all: ['calendar'] as const,
    events: (venueId: string) => ['calendar', 'events', venueId] as const,
  },

  gmReports: {
    all: ['gmReports'] as const,
    list: (venueId: string) => ['gmReports', 'list', venueId] as const,
    forEvent: (eventId: string) => ['gmReports', 'event', eventId] as const,
  },

  // Dashboard & analytics
  dashboard: {
    all: ['dashboard'] as const,
    analytics: (venueId: string) => ['dashboard', 'analytics', venueId] as const,
    weeklyHighlights: (venueId: string) => ['dashboard', 'weeklyHighlights', venueId] as const,
  },

  operatingCosts: {
    all: ['operatingCosts'] as const,
    forVenue: (venueId: string) => ['operatingCosts', venueId] as const,
  },

  // Marketing
  emailCampaigns: {
    all: ['emailCampaigns'] as const,
    list: (venueId: string) => ['emailCampaigns', 'list', venueId] as const,
  },

  smsCampaigns: {
    all: ['smsCampaigns'] as const,
    list: (venueId: string) => ['smsCampaigns', 'list', venueId] as const,
  },

  metaAds: {
    all: ['metaAds'] as const,
    campaigns: (venueId: string) => ['metaAds', 'campaigns', venueId] as const,
    analytics: (venueId: string) => ['metaAds', 'analytics', venueId] as const,
  },

  googleAds: {
    all: ['googleAds'] as const,
    analytics: (venueId: string) => ['googleAds', 'analytics', venueId] as const,
  },

  socialAnalytics: {
    all: ['socialAnalytics'] as const,
    instagram: (venueId: string) => ['socialAnalytics', 'instagram', venueId] as const,
  },

  eventOverlay: {
    all: ['eventOverlay'] as const,
    analytics: (venueId: string) => ['eventOverlay', 'analytics', venueId] as const,
  },

  socialPosts: {
    all: ['socialPosts'] as const,
    list: (venueId: string) => ['socialPosts', 'list', venueId] as const,
  },

  audiences: {
    all: ['audiences'] as const,
    list: (venueId: string) => ['audiences', 'list', venueId] as const,
  },

  // Artists & discovery
  artists: {
    all: ['artists'] as const,
    hyped: (venueId: string) => ['artists', 'hyped', venueId] as const,
    detail: (artistId: string) => ['artists', 'detail', artistId] as const,
  },

  watchlist: {
    all: ['watchlist'] as const,
    forVenue: (venueId: string) => ['watchlist', venueId] as const,
  },

  // CRM / VIP
  vip: {
    all: ['vip'] as const,
    list: (venueId: string) => ['vip', 'list', venueId] as const,
    detail: (vipId: string) => ['vip', 'detail', vipId] as const,
    alerts: (venueId: string) => ['vip', 'alerts', venueId] as const,
    alertsForProfile: (venueId: string, profileId: string) =>
      ['vip', 'alerts', venueId, profileId] as const,
    activity: (profileId: string) => ['vip', 'activity', profileId] as const,
    preferences: (profileId: string) => ['vip', 'preferences', profileId] as const,
    settings: (venueId: string) => ['vip', 'settings', venueId] as const,
  },

  // Inbox & notifications
  inbox: {
    all: ['inbox'] as const,
    channels: (venueId: string) => ['inbox', 'channels', venueId] as const,
    messages: (channelId: string) => ['inbox', 'messages', channelId] as const,
    pinned: (venueId: string) => ['inbox', 'pinned', venueId] as const,
    thread: (messageId: string) => ['inbox', 'thread', messageId] as const,
  },

  notifications: {
    all: ['notifications'] as const,
    forUser: (userId: string) => ['notifications', userId] as const,
  },

  // Insights & radar
  insights: {
    all: ['insights'] as const,
    strategic: (venueId: string) => ['insights', 'strategic', venueId] as const,
    eventRadar: (venueId: string) => ['insights', 'eventRadar', venueId] as const,
  },

  // Integrations
  integrations: {
    all: ['integrations'] as const,
    forUser: (userId: string) => ['integrations', userId] as const,
    forVenue: (venueId: string) => ['integrations', venueId] as const,
    connected: (userId: string) => ['integrations', 'connected', userId] as const,
    eventbrite: (venueId: string) => ['integrations', 'eventbrite', venueId] as const,
  },

  integrationHealth: {
    all: ['integrationHealth'] as const,
    forVenue: (venueId: string) => ['integrationHealth', venueId] as const,
  },

  syncHistory: {
    all: ['syncHistory'] as const,
    forProvider: (venueId: string, provider: string) => ['syncHistory', venueId, provider] as const,
  },

  // Syndication
  syndication: {
    all: ['syndication'] as const,
    destinations: (venueId: string) => ['syndication', 'destinations', venueId] as const,
    activity: (venueId: string) => ['syndication', 'activity', venueId] as const,
    detail: (eventId: string) => ['syndication', 'detail', eventId] as const,
  },

  // Marketing OS
  marketing: {
    all: ['marketing'] as const,
    overview: (venueId: string) => ['marketing', 'overview', venueId] as const,
    settings: (venueId: string) => ['marketing', 'settings', venueId] as const,
    emailCredits: (venueId: string) => ['marketing', 'emailCredits', venueId] as const,
    metaConnection: (venueId: string) => ['marketing', 'metaConnection', venueId] as const,
  },

  instagramCampaigns: {
    all: ['instagramCampaigns'] as const,
    list: (venueId: string) => ['instagramCampaigns', 'list', venueId] as const,
  },

  smsCredits: {
    all: ['smsCredits'] as const,
    forVenue: (venueId: string) => ['smsCredits', venueId] as const,
  },

  // Team
  team: {
    all: ['team'] as const,
    members: (venueId: string) => ['team', 'members', venueId] as const,
  },

  // Onboarding
  onboarding: {
    all: ['onboarding'] as const,
    status: (userId: string) => ['onboarding', 'status', userId] as const,
  },

  // Competitor Events
  competitorEvents: {
    all: ['competitorEvents'] as const,
    nearby: (venueId: string) => ['competitorEvents', 'nearby', venueId] as const,
    search: (venueId: string, params: string) =>
      ['competitorEvents', 'search', venueId, params] as const,
    byCity: (city: string) => ['competitorEvents', 'city', city] as const,
    upcomingByCity: (city: string, days: number) =>
      ['competitorEvents', 'city', city, 'upcoming', days] as const,
    scrapeFreshness: (city: string) => ['competitorEvents', 'scrapeFreshness', city] as const,
  },

  // POS (Point of Sale)
  pos: {
    all: ['pos'] as const,
    dailySummary: (venueId: string) => ['pos', 'daily', venueId] as const,
    revenueBreakdown: (venueId: string, date: string) => ['pos', 'revenue', venueId, date] as const,
    syncStatus: (venueId: string) => ['pos', 'syncStatus', venueId] as const,
  },

  // AI Copilot
  aiConversations: {
    all: ['aiConversations'] as const,
    latest: (userId: string, venueId: string) =>
      ['aiConversations', 'latest', userId, venueId] as const,
  },

  // Admin
  admin: {
    all: ['admin'] as const,
    schema: ['admin', 'schema'] as const,
    superAdmin: ['admin', 'superAdmin'] as const,
    auditLog: (page: number) => ['admin', 'auditLog', page] as const,
    tableRows: (tableName: string, page: number) =>
      ['admin', 'tableRows', tableName, page] as const,
  },

  // Entitlements
  entitlements: {
    all: ['entitlements'] as const,
    forVenue: (venueId: string) => ['entitlements', venueId] as const,
    forFeature: (venueId: string, featureKey: string) =>
      ['entitlements', venueId, featureKey] as const,
  },

  // Geocoding
  geocoding: {
    all: ['geocoding'] as const,
    forVenue: (venueId: string) => ['geocoding', venueId] as const,
  },

  // Weather
  weather: {
    all: ['weather'] as const,
    current: (venueId: string) => ['weather', 'current', venueId] as const,
    forecast: (venueId: string, date: string) => ['weather', 'forecast', venueId, date] as const,
  },
} as const;
